import { ChangeDetectionStrategy, Component, ViewEncapsulation, ContentChildren, QueryList, AfterContentInit, Input, TemplateRef, OnInit,
    OnDestroy, ChangeDetectorRef, AfterViewInit, Output, EventEmitter, SimpleChanges, OnChanges, ViewChild, HostBinding, ElementRef,
    Renderer2 } from '@angular/core';
import { Subject, combineLatest, of, Subscription, fromEvent } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

import { EuiTemplateDirective } from '../../directives';
import { EuiTableSortService } from './services/eui-table-sort.service';
import { EuiTablePaginatorComponent } from './paginator/eui-table-paginator.component';
import { PaginationEvent } from './models/pagination-event.model';
import { EuiTableFilterComponent } from './filter/eui-table-filter.component';
import { EuiTableSelectableRowService } from './services/eui-table-selectable-row.service';
import { SortEvent } from './models/sort-event.model';

import { BaseDirective } from '../../base.directive';

@Component({
    selector: 'eui-table, table[euiTable]',
    templateUrl: './eui-table.component.html',
    styleUrls: ['./styles/_index.scss'],
    host: {
        'class': 'eui-table',
    },
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [EuiTableSortService, EuiTableSelectableRowService],
})
export class EuiTableComponent extends BaseDirective implements AfterContentInit, OnInit, OnDestroy, AfterViewInit, OnChanges {

    @Input() rows: any[];
    @Input() paginator: EuiTablePaginatorComponent;
    @Input() filter: EuiTableFilterComponent;
    @Input() loading = false;
    @Input() preselectedRows: any[] = [];

    @Input()
    get asyncTable() { return this._asyncTable; }
    set asyncTable(value) { this._asyncTable = coerceBooleanProperty(value); }
    _asyncTable = false;

    @Input()
    get paginable() { return this._paginable; }
    set paginable(value) { this._paginable = coerceBooleanProperty(value); }
    _paginable = false;

    @Input()
    get euiTableResponsive() { return this._euiTableResponsive; }
    set euiTableResponsive(value) { this._euiTableResponsive = coerceBooleanProperty(value); }
    _euiTableResponsive = false;

    @Input()
    get euiTableFixedLayout() { return this._euiTableFixedLayout; }
    set euiTableFixedLayout(value) { this._euiTableFixedLayout = coerceBooleanProperty(value); }
    _euiTableFixedLayout = false;

    @Input()
    get euiTableDraggable() { return this._euiTableDraggable; }
    set euiTableDraggable(value) { this._euiTableDraggable = coerceBooleanProperty(value); }
    _euiTableDraggable = false;

    @Input()
    get euiTableCards() { return this._euiTableCards; }
    set euiTableCards(value) { this._euiTableCards = coerceBooleanProperty(value); }
    _euiTableCards = false;

    @Input()
    get euiTableBordered() { return this._euiTableBordered; }
    set euiTableBordered(value) { this._euiTableBordered = coerceBooleanProperty(value); }
    _euiTableBordered = false;

    @Input()
    get euiTableCompact() { return this._euiTableCompact; }
    set euiTableCompact(value) { this._euiTableCompact = coerceBooleanProperty(value); }
    _euiTableCompact = false;

    @Input()
    get hasStickyHeader() { return this._hasStickyHeader; }
    set hasStickyHeader(value) { this._hasStickyHeader = coerceBooleanProperty(value); }
    _hasStickyHeader = false;

    @Input() scrollableContainerRef: any;

    @Output() selectedRows = new EventEmitter<any[]>();
    @Output() sortChange = new EventEmitter<SortEvent>();

    public headerTemplate: TemplateRef<any>;
    public bodyTemplate: TemplateRef<any>;
    public footerTemplate: TemplateRef<any>;

    public showStickyHeader = false;

    @ContentChildren(EuiTemplateDirective) templates: QueryList<EuiTemplateDirective>;

    @ViewChild('stickyHeaderRef') stickyHeaderRef: ElementRef<HTMLElement>;
    @ViewChild('theadRef') theadRef: ElementRef<HTMLElement>;

    private rowsSrc: any = null;
    private destroy$: Subject<boolean> = new Subject<boolean>();
    private tableFeaturesHandlerSubscription = new Subscription();
    private selectRowsFeatureHandlerSubscription = new Subscription();
    private stickyHeaderHandlerSubscription = new Subscription();

    private stickyHeaderContainerScrollTop = 0;

    @HostBinding('class')
    get cssClasses() {
        return [
            this.euiTableResponsive ? `eui-table--responsive` : ``,
            this.euiTableFixedLayout ? `eui-table--fixed-layout` : ``,
            this.euiTableBordered ? `eui-table--bordered` : ``,
            this.euiTableCompact ? `eui-table--compact` : ``,
            this.euiTableDraggable ? `eui-table--draggable` : ``,
            this.euiTableCards ? `eui-table-cards` : ``,
            this.hasStickyHeader ? `eui-table--sticky-header` : ``,
            super.getCssClasses('eui-table'),
        ].join(' ').trim();
    }

    constructor(private euiTableSortService: EuiTableSortService,
                private cd: ChangeDetectorRef,
                private renderer: Renderer2,
                private euiTableSelectableRowService: EuiTableSelectableRowService) {
        super();
    }

    ngOnChanges(c: SimpleChanges): void {
        if (c && c.rows && c.rows.firstChange === false) {
            this.tableFeaturesHandlerSubscription.unsubscribe();
            this.stickyHeaderHandlerSubscription.unsubscribe();

            // Go to first page only if length of data has changed. To avoid user to be on a page that no longer exist in case of filtering.
            const lengthChanged = this.rowsSrc.length !== c.rows.currentValue.length;

            this.rowsSrc = [...c.rows.currentValue];
            if (this.paginator && lengthChanged) {
                this.paginator.goFirstPage();
            }
            if (!this.asyncTable) {
                this.tableFeaturesHandler();
            }
        }
        if (c && c.preselectedRows && c.preselectedRows.firstChange === false) {
            this.euiTableSelectableRowService.selectRows(this.preselectedRows);
        }
    }

    ngOnInit(): void {
        this.rowsSrc = [...this.rows];
        if (this.paginator) {
            this.paginator.setLength(this.rowsSrc.length);
        }
    }

    ngAfterContentInit(): void {
        if (!this.e2eAttr) {
            this.e2eAttr = 'eui-table';
        }
        this.templates.forEach((item) => {
            if (item.getType() === 'header') {
                this.headerTemplate = item.template;
            }
            if (item.getType() === 'body') {
                this.bodyTemplate = item.template;
            }
            if (item.getType() === 'footer') {
                this.footerTemplate = item.template;
            }
        });
    }

    ngAfterViewInit(): void {
        if (!this.asyncTable) {
            this.tableFeaturesHandlerSubscription.unsubscribe();
            this.tableFeaturesHandler();
        }

        this.euiTableSortService.sortingParams$.pipe(
            takeUntil(this.destroy$),
        ).subscribe((sortingParams: { sort: (string | null), order: string }) => {
            setTimeout(() => { this.sortChange.emit({ sort: sortingParams.sort, order: sortingParams.order }); });
        });

        this.selectRowsFeatureHandler();

        setTimeout(() => { this.euiTableSelectableRowService.selectRows(this.preselectedRows); });

        if (this.hasStickyHeader) {
            this.stickyHeaderHandler();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    public trackByFn(index: number, item: any): number {
        return index;
    }

    public selectAllRows(): void {
        this.euiTableSelectableRowService.selectAllRows();
    }

    public unselectAllRows(): void {
        this.euiTableSelectableRowService.unselectAllRows();
    }

    public onContentChanges(): void {
        if (this.hasStickyHeader) {
            this.stickyHeaderHandler();
        }
    }

    private stickyHeaderHandler(): void {
        this.stickyHeaderHandlerSubscription = fromEvent(this.scrollableContainerRef, 'scroll').pipe(
            startWith({ target: { scrollTop: this.stickyHeaderContainerScrollTop } }),
            takeUntil(this.destroy$),
        ).subscribe((e: Event) => {
            this.stickyHeaderContainerScrollTop = e.target['scrollTop'];

            const theadRefThItems = this.theadRef.nativeElement.querySelectorAll('th');
            const stickyHeaderRefThItems = this.stickyHeaderRef.nativeElement.querySelectorAll('th');

            if (e.target['scrollTop'] > 0) {
                this.showStickyHeader = true;
                this.renderer.setStyle(this.stickyHeaderRef.nativeElement, 'display', 'block');
            } else {
                this.showStickyHeader = false;
                this.renderer.setStyle(this.stickyHeaderRef.nativeElement, 'display', 'none');
            }

            stickyHeaderRefThItems.forEach((stickyHeaderRefThItem, index) => {
                const domRect = theadRefThItems[index].getBoundingClientRect();
                this.renderer.setStyle(stickyHeaderRefThItem, 'width', domRect.width + 'px');
                this.renderer.setStyle(stickyHeaderRefThItem, 'height', domRect.height + 'px');
            });

            this.renderer.setStyle(this.stickyHeaderRef.nativeElement, 'top', e.target['scrollTop'] + 'px');
            this.cd.detectChanges();
        });
    }

    private tableFeaturesHandler(): void {
        this.tableFeaturesHandlerSubscription = combineLatest([
            this.euiTableSortService.sortingParams$,
            this.paginator ? this.paginator.page$ : of(null),
            this.filter ? this.filter.filter$ : of(null),
        ]).pipe(takeUntil(this.destroy$)).subscribe(([sortingParams, pagination, filter]) => {
            let r = [...this.rowsSrc];
            r = this.orderRows(sortingParams.sort, sortingParams.order, r);
            r = this.filterRows(filter, r);
            if (this.paginator) {
                this.paginator.setLength(r.length);
                r = this.paginateRows(pagination, r);
            }

            this.rows = r;

            this.cd.detectChanges();
        });
    }

    private selectRowsFeatureHandler(): void {
        this.selectRowsFeatureHandlerSubscription = this.euiTableSelectableRowService.selectedRows$.pipe(
            takeUntil(this.destroy$),
        ).subscribe((selected: any[]) => {
            this.selectedRows.emit(selected);
        });
    }

    private filterRows(filter: string, r: any[]): any[] {
        const rows = filter ? r.filter((row: any) => {
            const keys = Object.keys(row);
            const parseKeys = this.parseKeys(keys, row, filter);

            // console.log(parseKeys)
            return parseKeys.filter(parsedKey => parsedKey.match === true).length > 0;
        }) : r;

        return rows;
    }

    private parseKeys(keys: string[], row: any, filter: string, parent: string[] = [], matches = []): { key: string, match: boolean }[] {
        keys.forEach((key) => {
            let prop = parent.join('.');

            if (row[key] && Array.isArray(row[key])) {
                if (JSON.stringify(row[key]).toString().toLowerCase().indexOf(filter.toLowerCase()) !== -1) {
                    matches.push({ key, match: true });
                } else {
                    matches.push({ key, match: false });
                }
            }

            if (row[key] && typeof(row[key]) !== 'object') {
                if (row[key].toString().toLowerCase().indexOf(filter.toLowerCase()) !== -1) {
                    matches.push({ key, match: true });
                } else {
                    matches.push({ key, match: false });
                }
            }

            if (row[key] && typeof(row[key]) === 'object' && !Array.isArray(row[key])) {
                parent.push(key);

                const k = Object.keys(row[key]);
                this.parseKeys(k, row, filter, parent, matches);
            }

            if (parent.length > 0) {
                let objProp = this.getObjProp(row, prop + '.' + key);

                if (!objProp) {
                    for (let i = parent.length; i >= 1; i --) {
                        prop = parent.slice(0, i).join('.');
                        if (this.getObjProp(row, prop + '.' + key)) {
                            objProp = this.getObjProp(row, prop + '.' + key);
                            break;
                        }
                    }
                }

                if (objProp && Array.isArray(objProp)) {
                    if (JSON.stringify(objProp).toString().toLowerCase().indexOf(filter.toLowerCase()) !== -1) {
                        matches.push({ key: prop + '.' + key, match: true });
                    } else {
                        matches.push({ key: prop + '.' + key, match: false });
                    }
                }

                if (objProp && typeof(objProp) !== 'object') {
                    if (objProp.toString().toLowerCase().indexOf(filter.toLowerCase()) !== -1) {
                        matches.push({ key: prop + '.' + key, match: true });
                    } else {
                        matches.push({ key: prop + '.' + key, match: false });
                    }
                }

                if (objProp && typeof(objProp) === 'object') {
                    parent.push(key);

                    const k = Object.keys(objProp);
                    this.parseKeys(k, row, filter, parent, matches);
                }
            }
        });

        return matches;
    }

    private paginateRows(pagination: PaginationEvent, r: any[]): any[] {
        const page = pagination.page;
        const pageSize = pagination.pageSize;
        const rows = r.slice(page * pageSize, (page * pageSize) + pageSize);

        return rows;
    }

    private orderRows(prop: string, order: string, r: any[]): any[] {
        let rows: any[] = null;
        if (!order) {
            rows = r;
        } else {
            if (order === 'desc') {
                rows = r.sort((a: any, b: any) => {

                    const aSortOn = this.getObjProp(a, prop);
                    const bSortOn = this.getObjProp(b, prop);

                    if (aSortOn < bSortOn) { return 1; }
                    if (aSortOn > bSortOn) { return -1; }
                    return 0;
                });
            } else {
                rows = r.sort((a: any, b: any) => {

                    const aSortOn = this.getObjProp(a, prop);
                    const bSortOn = this.getObjProp(b, prop);

                    if (aSortOn > bSortOn) { return 1; }
                    if (aSortOn < bSortOn) { return -1; }
                    return 0;
                });
            }
        }

        return rows;
    }

    private getObjProp(obj: any, prop: string): string {
        return prop ? prop.split('.').reduce((prev, curr) => {
            return prev ? prev[curr] : null;
        }, obj || self) : null;
    }
}
